import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  InputAdornment,
} from '@mui/material';
import {
  Edit as EditIcon,
  History as HistoryIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import api from '../../services/api';

const PaymentHistory = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPayment, setEditingPayment] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editReason, setEditReason] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [reverseDialogOpen, setReverseDialogOpen] = useState(false);
  const [reverseRequestDialogOpen, setReverseRequestDialogOpen] = useState(false);
  const [reversingPayment, setReversingPayment] = useState(null);
  const [reverseReason, setReverseReason] = useState('');
  const [canReverseDirectly, setCanReverseDirectly] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [filterLoanId, setFilterLoanId] = useState('');
  const [filterCustomerName, setFilterCustomerName] = useState('');

  useEffect(() => {
    if (user?.userId) {
      fetchPaymentHistory();
    }
  }, [user]);

  const fetchPaymentHistory = async () => {
    setLoading(true);
    try {
      const response = await api.get('/collector/payment-history', {
        params: { collectorId: user.userId }
      });
      setPaymentHistory(response.data || []);
    } catch (error) {
      console.error('Error fetching payment history:', error);
      showToast('Failed to load payment history', 'error');
    } finally {
      setLoading(false);
    }
  };

  const isEditable = (payment) => {
    // Check if payment is already reversed
    if (payment.reversed) return false;
    
    // Check 24-hour rule
    if (!payment.createdAt) return true;
    try {
      // Parse the createdAt timestamp (in Sri Lanka timezone from backend)
      const paymentTime = new Date(payment.createdAt);
      if (isNaN(paymentTime.getTime())) return true; // Invalid date, allow edit
      
      const now = new Date();
      const hoursSincePayment = (now - paymentTime) / (1000 * 60 * 60);
      return hoursSincePayment <= 24;
    } catch (error) {
      console.error('Error checking editability:', error);
      return true; // On error, allow edit
    }
  };

  const handleEditClick = (payment) => {
    if (payment.reversed) {
      showToast('This payment has already been reversed and cannot be edited.', 'warning');
      return;
    }
    
    if (isEditable(payment)) {
      setEditingPayment(payment);
      setEditAmount(payment.amount?.toString() || '');
      setEditNotes(payment.notes || '');
      setEditReason('');
      setEditDialogOpen(true);
    } else {
      // Show request dialog for payments older than 24 hours
      setEditingPayment(payment);
      setEditAmount(payment.amount?.toString() || '');
      setEditNotes(payment.notes || '');
      setEditReason('');
      setRequestDialogOpen(true);
    }
  };

  const handleReverseClick = async (payment) => {
    if (payment.reversed) {
      showToast('This payment has already been reversed.', 'warning');
      return;
    }

    setReversingPayment(payment);
    setReverseReason('');

    // Check if payment can be reversed directly
    try {
      const response = await api.get(`/collector/payment-history/${payment.id}/can-reverse`);
      setCanReverseDirectly(response.data.canReverse);
      
      if (response.data.canReverse) {
        setReverseDialogOpen(true);
      } else {
        setReverseRequestDialogOpen(true);
      }
    } catch (error) {
      console.error('Error checking if payment can be reversed:', error);
      // If check fails, assume it needs admin approval
      setCanReverseDirectly(false);
      setReverseRequestDialogOpen(true);
    }
  };

  const handleReversePayment = async () => {
    if (!reversingPayment || !reverseReason || reverseReason.trim() === '') {
      showToast('Please provide a reason for reversing this payment', 'error');
      return;
    }

    setProcessing(true);
    try {
      await api.post(`/collector/payment-history/${reversingPayment.id}/reverse`, null, {
        params: {
          collectorId: user.userId,
          reason: reverseReason
        }
      });
      
      showToast('Payment reversed successfully', 'success');
      setReverseDialogOpen(false);
      setReversingPayment(null);
      setReverseReason('');
      
      // Wait a moment for backend to finish processing
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Refresh payment history first
      await fetchPaymentHistory();
      
      // Refresh the page to update all calculations in Search and Collect
      // This ensures Paid Amount and other calculations are updated
      window.location.reload();
    } catch (error) {
      console.error('Error reversing payment:', error);
      showToast(error.response?.data?.error || 'Failed to reverse payment', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleSubmitReverseRequest = async () => {
    if (!reversingPayment || !reverseReason || reverseReason.trim() === '') {
      showToast('Please provide a reason for reversing this payment', 'error');
      return;
    }

    setProcessing(true);
    try {
      await api.post('/collector/payment-reversal-requests', null, {
        params: {
          paymentHistoryId: reversingPayment.id,
          requestedBy: user.userId,
          reason: reverseReason
        }
      });
      
      showToast('Reversal request submitted successfully. Waiting for admin approval.', 'success');
      setReverseRequestDialogOpen(false);
      setReversingPayment(null);
      setReverseReason('');
      await fetchPaymentHistory();
      
      // Note: For admin-approved reversals, the page will need to be refreshed manually
      // or the admin can notify the field officer to refresh
    } catch (error) {
      console.error('Error submitting reversal request:', error);
      showToast(error.response?.data?.error || 'Failed to submit reversal request', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editAmount || parseFloat(editAmount) <= 0) {
      showToast('Please enter a valid payment amount', 'error');
      return;
    }

    setProcessing(true);
    try {
      await api.put(`/collector/payment-history/${editingPayment.id}`, null, {
        params: {
          amount: parseFloat(editAmount),
          notes: editNotes || undefined,
          requireAdminApproval: false
        }
      });
      
      showToast('Payment updated successfully', 'success');
      setEditDialogOpen(false);
      setEditingPayment(null);
      setEditAmount('');
      setEditNotes('');
      await fetchPaymentHistory();
    } catch (error) {
      console.error('Error updating payment:', error);
      showToast(error.response?.data?.error || 'Failed to update payment', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleSubmitEditRequest = async () => {
    if (!editAmount || parseFloat(editAmount) <= 0) {
      showToast('Please enter a valid payment amount', 'error');
      return;
    }

    if (!editReason || editReason.trim() === '') {
      showToast('Please provide a reason for the edit request', 'error');
      return;
    }

    setProcessing(true);
    try {
      await api.post(`/collector/payment-history/${editingPayment.id}/edit-request`, null, {
        params: {
          requestedBy: user.userId,
          requestedPaymentAmount: parseFloat(editAmount),
          reason: editReason
        }
      });
      
      showToast('Edit request submitted successfully. Waiting for admin approval.', 'success');
      setRequestDialogOpen(false);
      setEditingPayment(null);
      setEditAmount('');
      setEditNotes('');
      setEditReason('');
      await fetchPaymentHistory();
    } catch (error) {
      console.error('Error submitting edit request:', error);
      showToast(error.response?.data?.error || 'Failed to submit edit request', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'Rs 0.00';
    return `Rs ${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      // Parse the date string (handles both ISO strings and date-only strings)
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      
      // Format with date and time in Sri Lanka timezone (Asia/Colombo)
      return date.toLocaleString('en-LK', {
        timeZone: 'Asia/Colombo',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  const filteredHistory = paymentHistory.filter(payment => {
    const loanIdMatch = !filterLoanId || 
      (payment.installment?.loan?.id?.toString().includes(filterLoanId));
    const customerMatch = !filterCustomerName || 
      (payment.installment?.loan?.customer?.fullName?.toLowerCase().includes(filterCustomerName.toLowerCase()));
    return loanIdMatch && customerMatch;
  });

  // Compute latest payment by installment once
  const latestPaymentByInstallment = useMemo(() => {
    const latestByInstallment = {};
    filteredHistory.forEach(payment => {
      const installmentId = payment.installment?.id;
      if (!installmentId) return;
      
      if (!latestByInstallment[installmentId]) {
        latestByInstallment[installmentId] = payment;
      } else {
        const existing = latestByInstallment[installmentId];
        const existingTime = existing.createdAt ? new Date(existing.createdAt).getTime() : 0;
        const currentTime = payment.createdAt ? new Date(payment.createdAt).getTime() : 0;
        
        if (currentTime > existingTime) {
          latestByInstallment[installmentId] = payment;
        }
      }
    });
    return latestByInstallment;
  }, [filteredHistory]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976D2' }}>
          <HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Payment History
        </Typography>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            All Payment Records
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            IMPORTANT: All payment history records are always displayed. No records are hidden or removed.
            All past payments remain visible at all times for audit and tracking purposes.
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              label="Filter by Loan ID"
              value={filterLoanId}
              onChange={(e) => setFilterLoanId(e.target.value)}
              size="small"
              sx={{ minWidth: 200 }}
            />
            <TextField
              label="Filter by Customer Name"
              value={filterCustomerName}
              onChange={(e) => setFilterCustomerName(e.target.value)}
              size="small"
              sx={{ minWidth: 200 }}
            />
          </Box>

          {filteredHistory.length === 0 ? (
            <Alert severity="info">
              {paymentHistory.length === 0 
                ? 'No payment history found. Payments will appear here after they are recorded.'
                : 'No payments match the current filters.'}
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Date & Time</strong></TableCell>
                    <TableCell><strong>Loan ID</strong></TableCell>
                    <TableCell><strong>Customer</strong></TableCell>
                    <TableCell><strong>Installment #</strong></TableCell>
                    <TableCell align="right"><strong>Amount</strong></TableCell>
                    <TableCell><strong>Collector</strong></TableCell>
                    <TableCell><strong>Notes</strong></TableCell>
                    <TableCell align="center"><strong>Status</strong></TableCell>
                    <TableCell align="center"><strong>Actions</strong></TableCell>
                    <TableCell align="center"><strong>Reverse</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredHistory.map((payment) => {
                    const editable = isEditable(payment);
                    const hoursSincePayment = payment.createdAt 
                      ? Math.floor((new Date() - new Date(payment.createdAt)) / (1000 * 60 * 60))
                      : 0;
                    
                    return (
                      <TableRow key={payment.id} hover>
                        <TableCell>{formatDate(payment.createdAt || payment.paymentDate)}</TableCell>
                        <TableCell>
                          {payment.installment?.loan?.id || 'N/A'}
                        </TableCell>
                        <TableCell>
                          {payment.installment?.loan?.customer?.fullName || 'N/A'}
                        </TableCell>
                        <TableCell>
                          {payment.installment?.installmentNumber || 'N/A'}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(payment.amount)}
                        </TableCell>
                        <TableCell>
                          {payment.collector?.fullName || 'N/A'}
                        </TableCell>
                        <TableCell>
                          {payment.notes || '-'}
                        </TableCell>
                        <TableCell align="center">
                          {payment.reversed ? (
                            <Chip label="Reversed" color="error" size="small" />
                          ) : editable ? (
                            <Chip label="Editable" color="success" size="small" />
                          ) : (
                            <Chip 
                              label={`Requires Admin Approval (${hoursSincePayment}h ago)`}
                              color="warning" 
                              size="small" 
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {payment.reversed ? (
                            <Chip 
                              label="Reversed" 
                              size="small" 
                              color="error"
                              variant="outlined"
                            />
                          ) : editable ? (
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEditClick(payment)}
                              title="Edit Payment (within 24 hours)"
                            >
                              <EditIcon />
                            </IconButton>
                          ) : (
                            <Button
                              variant="contained"
                              color="warning"
                              size="small"
                              startIcon={<AdminPanelSettingsIcon />}
                              onClick={() => handleEditClick(payment)}
                              title="This payment is older than 24 hours. Admin approval is required to edit."
                              sx={{
                                textTransform: 'none',
                                fontWeight: 'bold',
                              }}
                            >
                              Request Edit
                            </Button>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {payment.reversed ? (
                            <Chip 
                              label="Already Reversed" 
                              size="small" 
                              color="error"
                              variant="outlined"
                            />
                          ) : (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleReverseClick(payment)}
                              title="Reverse Payment"
                            >
                              <UndoIcon />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog (for payments within 24 hours) */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Payment</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            This payment is within 24 hours and can be edited directly.
          </Alert>
          {editingPayment && (
            <Box>
              <TextField
                fullWidth
                label="Payment Amount"
                type="number"
                value={editAmount}
                onChange={(e) => setEditAmount(e.target.value)}
                sx={{ mb: 2, mt: 2 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">Rs</InputAdornment>
                }}
              />
              <TextField
                fullWidth
                label="Notes (Optional)"
                multiline
                rows={3}
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={processing}
            startIcon={processing ? <CircularProgress size={20} /> : null}
          >
            {processing ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Request Dialog (for payments older than 24 hours) */}
      <Dialog open={requestDialogOpen} onClose={() => setRequestDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AdminPanelSettingsIcon color="warning" />
            Request Payment Edit - Admin Approval Required
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>This payment is older than 24 hours.</strong> Admin approval is required to edit it. 
            Your request will be submitted for review.
          </Alert>
          {editingPayment && (
            <Box>
              <TextField
                fullWidth
                label="Requested Payment Amount"
                type="number"
                value={editAmount}
                onChange={(e) => setEditAmount(e.target.value)}
                sx={{ mb: 2, mt: 2 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">Rs</InputAdornment>
                }}
              />
              <TextField
                fullWidth
                label="Notes (Optional)"
                multiline
                rows={2}
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Reason for Edit *"
                multiline
                rows={3}
                value={editReason}
                onChange={(e) => setEditReason(e.target.value)}
                required
                helperText="Please provide a reason for requesting this edit"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmitEditRequest}
            disabled={processing || !editReason || editReason.trim() === ''}
            startIcon={processing ? <CircularProgress size={20} /> : null}
          >
            {processing ? 'Submitting...' : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reverse Payment Dialog (for payments within 24 hours) */}
      <Dialog open={reverseDialogOpen} onClose={() => setReverseDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <UndoIcon color="error" />
            Reverse Payment
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Warning:</strong> This payment is within 24 hours and can be reversed directly. 
            This action will mark the payment as reversed and recalculate all related installments.
          </Alert>
          {reversingPayment && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Payment Amount: <strong>{formatCurrency(reversingPayment.amount)}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Loan ID: <strong>{reversingPayment.installment?.loan?.id || 'N/A'}</strong> | 
                Installment: <strong>#{reversingPayment.installment?.installmentNumber || 'N/A'}</strong>
              </Typography>
              <TextField
                fullWidth
                label="Reason for Reversal *"
                multiline
                rows={4}
                value={reverseReason}
                onChange={(e) => setReverseReason(e.target.value)}
                required
                helperText="Please provide a reason for reversing this payment"
                sx={{ mt: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReverseDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReversePayment}
            disabled={processing || !reverseReason || reverseReason.trim() === ''}
            startIcon={processing ? <CircularProgress size={20} /> : <UndoIcon />}
          >
            {processing ? 'Reversing...' : 'Reverse Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reverse Request Dialog (for payments older than 24 hours) */}
      <Dialog open={reverseRequestDialogOpen} onClose={() => setReverseRequestDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AdminPanelSettingsIcon color="warning" />
            Request Payment Reversal - Admin Approval Required
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>This payment is older than 24 hours.</strong> Admin approval is required to reverse it. 
            Your request will be submitted for review.
          </Alert>
          {reversingPayment && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Payment Amount: <strong>{formatCurrency(reversingPayment.amount)}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Loan ID: <strong>{reversingPayment.installment?.loan?.id || 'N/A'}</strong> | 
                Installment: <strong>#{reversingPayment.installment?.installmentNumber || 'N/A'}</strong>
              </Typography>
              <TextField
                fullWidth
                label="Reason for Reversal *"
                multiline
                rows={4}
                value={reverseReason}
                onChange={(e) => setReverseReason(e.target.value)}
                required
                helperText="Please provide a reason for requesting this reversal"
                sx={{ mt: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReverseRequestDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleSubmitReverseRequest}
            disabled={processing || !reverseReason || reverseReason.trim() === ''}
            startIcon={processing ? <CircularProgress size={20} /> : <AdminPanelSettingsIcon />}
          >
            {processing ? 'Submitting...' : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentHistory;

